import { BottomSection, BreadCrumb, Button, ButtonRow, Docker, Filter, NoRecord, Popup, SelectMenu, Table, TabList, Textarea, Textbox } from 'components/generic';
import { useFetch } from 'hooks';
import { isNumber, sumBy } from 'lodash';
import { useEffect, useMemo, useState, useCallback, useContext } from 'react';
import { dockerlist, TemplatesSection } from 'config';
import { AnimatePresence, motion } from 'framer-motion';
import Images from 'assets/Images';
import { GeneralDetails, TemplateConfig } from './components';
import { TemplateContext } from 'providers/template-provider';
import { arrayBufferToBlob, getValuesById } from 'utils';
import { useSelector } from 'react-redux';
import './style.scss';

const Templates = () => {
	const api = useFetch();
	const context = useContext(TemplateContext);
	const { selectedTemplate, dockerState, createObj, activeSection, savePopupState, setSavePopupState, addPopupToggler, setSelectedTemplate, setDockerState, setCreateObj, setActiveSection, setAddPopupToggler, reset } = context;
	const [tableData, setTableData] = useState('');
	const [tableDataCount, setTableDataCount] = useState();
	const [tableHeaders, setTableHeaders] = useState([]);
	const [tableWidth, setTableWidth] = useState();
	const [tablePayload, setTablePayload] = useState({ offset: 0, limit: 20 });
	const [scrollbarEnable, setScrollbarEnable] = useState(false);
	const [scrollToTop, setScrollToTop] = useState(0);
	const [previewTemplateData, setPreviewTemplateData] = useState();
	const referenceData = useSelector(state => state?.referenceData)?.data;

	const templateType = getValuesById(referenceData, 'rd_templateType')?.at()?.value || [];

	const gridStructure = async () => {
		const { data } = await api({ url: 'gridStructure/v1/COMMUNICATION_TEMPLATE', method: 'get' });
		setTableWidth(`${sumBy(data?.data[0]?.columns, 'columnWidth')}px`);
		setTableHeaders(data?.data[0]?.columns);
	};

	const tableDataAPI = async (payload, reset) => {
		const apiReq = { filter: payload?.filter, sort: payload?.sort?.at(), latest: 'Y', templateTypes: ['sms', 'whatsapp', 'email'], offset: reset ? 0 : payload?.offset, limit: 20 };
		const { data } = await api({ url: 'list', template: true, method: 'post', data: apiReq });
		const elm = data?.data?.at();
		if (elm) {
			const res = elm?.result;
			setTableData(prevState => (reset ? res : [...new Set([...prevState, ...res])]));
			setScrollbarEnable(res?.length);
			setTableDataCount(elm?.total);
		}
	};

	useEffect(() => {
		gridStructure();
		tableDataAPI({}, true);
	}, []);

	const setPayload = (payload, reset) => {
		setTablePayload(prevState => {
			console.log(tablePayload)
			const data = { ...prevState, ...payload, offset: payload.incStartIndex ? prevState?.offset + 20 : 0, limit: 20 };
			tableDataAPI(data, reset);
			setScrollToTop(reset);
			return data;
		});
	};

	const handleFilter = e => {
		const data = e?.map(el => ({ key: el?.id, value: el?.value }));
		setPayload({ filter: data }, true);
	};

	const handleSort = e => {
		setPayload({ sort: [e] }, true);
	};

	const handleScroll = () => {
		setPayload({ incStartIndex: true }, false);
	};

	const handleCheckbox = value => {
		setSelectedTemplate(value);
	};

	const previewTemplate = async obj => {
		const { data } = await api({ url: `${obj?.templateId}/${obj?.verNum}/preview`, template: true, method: 'get' });

		const blob = arrayBufferToBlob(data?.data?.template_generatedPDF?.data?.data);
		setPreviewTemplateData(blob);
	};

	// docker click handler
	const handleDocker = e => {
		e?.key === 'cancel' ? reset() : setDockerState(e?.key);
		['edit', 'delete', 'view']?.includes(e?.key) && setCreateObj(tableData[selectedTemplate]);
		e?.key === 'delete' && setSavePopupState('delete');
		e?.key === 'view' && previewTemplate(tableData[selectedTemplate]);
	};

	const handleNewButton = () => {
		setAddPopupToggler(true);
		setSelectedTemplate(null);
	};

	const setAddHandler = () => {
		setActiveSection('template_configuration');
		setDockerState('add');
	};

	const handleButtonClick = e => {
		e === 'Goback' && reset();
		e === 'Create' && setAddHandler();
	};

	const dataHandler = (e, key) => {
		setCreateObj(prevState => ({ ...prevState, [key]: e }));
	};

	const createPopupContent = useMemo(() => {
		return (
			<Popup onClose={reset} popupState={addPopupToggler}>
				<div className='template__popup'>
					<h3>Create Template</h3>
					<Textbox label='Template Name' infoContent='Enter Template Name' mandatory placeholder='Enter template name' value={createObj?.templateName} onChange={e => dataHandler(e?.target?.value, 'templateName')} />
					<Textarea label='Template Description' infoContent='Enter Template Description' mandatory placeholder='Enter template description' value={createObj?.templateDescription} onChange={e => dataHandler(e?.target?.value, 'templateDescription')} />
					<SelectMenu label='Template Type' infoContent='Select Template Type' mandatory placeholder='Select' menuOptions={templateType} value={getValuesById(templateType, createObj?.templateType)} onChange={e => dataHandler(e, 'templateType')} />
					<ButtonRow label='Goback' label1='Create' right disableSave={!createObj?.templateName || !createObj?.templateDescription || !createObj?.templateType} handleButtonClick={handleButtonClick}></ButtonRow>
				</div>
			</Popup>
		);
	}, [addPopupToggler, createObj]);

	const handleTemplateCRUD = async () => {
		const url = ['edit', 'delete']?.includes(dockerState) ? `${createObj?.templateId}/${createObj?.verNum}` : '';
		let method = '';
		if (dockerState === 'add') {
			method = 'post';
		} else {
			method = dockerState === 'edit' ? 'put' : 'delete';
		}
		await api({ url, method, template: true, data: createObj, successToaster: true });
		await tableDataAPI({}, true);
		reset();
	};

	// popup confirmation on save and goback
	const handleSaveClickPopup = useCallback(
		e => {
			if (e === 'Yes') {
				['Save', 'delete']?.includes(savePopupState) && handleTemplateCRUD();
				savePopupState === 'Goback' && reset();
			} else if (e === 'No') {
				savePopupState === 'Save' && setSavePopupState(null);
				savePopupState === 'delete' && reset();
				savePopupState === 'Goback' && setSavePopupState(null);
			}
		},
		[savePopupState]
	);

	// handler for save and delete popup confirmations
	const handleSaveDeletePopup = e => {
		handleSaveClickPopup(e);
	};

	// popup confirmation content for save and delete
	const saveDeletePopupContent = ({ label, type }) => {
		return (
			<div className='bim__confirmation'>
				<img src={Images.warningPopup} alt='warning' />
				<h3>Warning</h3>
				<p>{label}</p>
				<ButtonRow label='No' label1='Yes' handleButtonClick={e => handleSaveDeletePopup(e)} />
			</div>
		);
	};

	const closePopup = type => {
		setSavePopupState(null);
		type === 'delete' && reset();
	};

	// popup content for going back and save
	const gobackSavePopup = useMemo(
		() => (
			<Popup onClose={() => closePopup('save')} popupState={savePopupState?.length > 0}>
				{saveDeletePopupContent({ label: ['Save', 'delete']?.includes(savePopupState) ? `Are you sure you want to ${savePopupState?.toLowerCase()}?` : 'Do you want to go back to dashboard?', type: savePopupState?.toLowerCase() })}
			</Popup>
		),
		[savePopupState]
	);

	const handleSectionClick = e => {
		setActiveSection(e?.key);
	};

	const handleBottomSection = e => {
		setSavePopupState(e);
	};

	// display the active section
	const displayActiveSection = useMemo(() => {
		const obj = {
			general_details: () => <GeneralDetails />,
			template_configuration: () => <TemplateConfig />,
			DEFAULT: () => null
		};
		return (obj[activeSection] || obj['DEFAULT'])();
	}, [activeSection]);

	return (
		<>
			{['listing', 'delete', 'view'].includes(dockerState) ? (
				<>
					<div className='template__container'>
						<div className='template__add flex-end'>
							<Filter filterObj={tableHeaders?.filter(el => el?.filterBy)} handleSubmit={handleFilter} />
							<Button primary compact label='Add new' handleOnSubmit={handleNewButton} />
						</div>
						{tableDataCount && (
							<>
								<div className='template__display'>
									<Table
										hideHeaderCheckbox
										checkboxWidth='70px'
										checkedItem={selectedTemplate}
										tableHeader={tableHeaders}
										tableData={tableData}
										tableCount={tableDataCount}
										scrollToTop={scrollToTop}
										tableWidth={tableWidth}
										handleSort={handleSort}
										infiniteScroll={scrollbarEnable}
										handleScroll={handleScroll}
										onCheckboxClick={handleCheckbox}
									/>
								</div>
								<p className='template__count'>Total Records {tableDataCount}</p>
							</>
						)}

						{!tableDataCount && <NoRecord header='No templates found!' description='Please configure new templates' height='90%' />}
					</div>
					{createPopupContent}

					<AnimatePresence mode='wait'>
						{isNumber(selectedTemplate) && (
							<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
								<Docker dockerClick={handleDocker} dockerlist={dockerlist} selectlist={[selectedTemplate]} />
							</motion.div>
						)}
					</AnimatePresence>
				</>
			) : (
				<>
					<BreadCrumb path={['Template Maintenance', dockerState, activeSection]} />
					<TabList tabList={TemplatesSection} tabActive={activeSection} handleTabClick={handleSectionClick} />
					<BottomSection handleButtonClick={handleBottomSection} disableSave={dockerState === 'view'} />

					<AnimatePresence mode='wait'>
						<motion.div key={activeSection} initial={{ x: -100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 100, opacity: 0 }} transition={{ duration: 0.1 }}>
							{displayActiveSection}
						</motion.div>
					</AnimatePresence>
				</>
			)}
			{gobackSavePopup}
			<Popup popupState={previewTemplateData} onClose={() => setPreviewTemplateData()}>
				<div style={{ width: '680px', height: '680px' }}>
					<embed style={{ width: '680px', height: '100%' }} src={previewTemplateData} />
				</div>
			</Popup>
		</>
	);
};

export default Templates;
